import {
    LeftOutlined,
    MinusOutlined,
    PlusOutlined,
    RightOutlined,
    DownOutlined,
} from '@ant-design/icons'
import { Input, Skeleton, Select, Spin } from 'antd'
import { isEmpty } from 'lodash'
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Document } from 'react-pdf'
import { useSelector } from 'react-redux'
import { VariableSizeList as List } from 'react-window'
import { GENERAL_GUIDELINES, CONTENT_TYPES, getPdfOptionsForPrograms, IS_IFRAMES, IS_GUIDELINE_GPT, convertS3PathToUrl } from './constants'
// import { useAllPrograms } from './hooks/useAllPrograms'
import './PDFViewer.css'
import Worker from './pdfWorker.js'
import useEdgeWheelDetect from './hooks/useScrollEdgeDetect'
import { ICON } from './components/GuidelineSelect/GuidelineCheckbox'
import { PageRender } from './render'
import useLoadStatus from './hooks/useLoadStatus'

import ICON_ERROR from './assets/error.svg';

const MIN_SCALE = 0.5
const MAX_SCALE = 3
const SCALE_STEP = 0.1
const PAGE_GAP = 0

// 找到映射的key
function getFilePathName (url){
  const  address= decodeURIComponent(url)
  const [_,filePath] = address.split('.com/')
  return filePath
}

function PDFViewer(props,ref) {
    const { file, jumpEmitter, mountEmitter, isMobile, setMobileAIChat, anomaly, isScenario, programs, isPdfLoading, onPdfLoadingChange } = props
    const { previewId } = props; // "previewId" 是"Scenario AI"预览模式，有"previewId"一定是预览模式

    // Add state for dynamic PDF handling
    const [currentPdfFile, setCurrentPdfFile] = useState(file)
    const lastProcessedFile = useRef(null)
    
    // Update currentPdfFile when file prop changes
    useEffect(() => {
        console.log('PDFViewer file prop changed:', file)
        setCurrentPdfFile(file)
    }, [file])

    const [numPages, setNumPages] = useState(null)
    const [currentPageNumber, setCurrentPageNumber] = useState(1)
    const [inputPageNumber, setInputPageNumber] = useState('1')
    const [highlightPageNumber, setHighlightPageNumber] = useState(null)
    const [highlightCoordinates, setHighlightCoordinates] = useState([])
    const [scale, setScale] = useState(1)
    const [isZooming, setIsZooming] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [pages, setPages] = useState([])
    const containerRef = useRef(null)
    const pageRefs = useRef({})
    const listRef = useRef(null)
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
    const [firstPageRendered, setFirstPageRendered] = useState(false)
    const [selectedSource, setSelectedSource] = useState(null)
    const [pendingJump, setPendingJump] = useState(null)
    const [showControls, setShowControls] = useState(true)

    const prevPages = useRef([])

    const contentType = useSelector((state) => state.common.content)
    const selectedLenders = useSelector((state) => state.common.source)

    // Fetch all programs from database for PDF options
    const dbPrograms = programs.data;
    
    const guidelineName = useMemo(() => {
        if (isEmpty(contentType)) return ''
        const guideline = GENERAL_GUIDELINES.find(
            (guideline) => guideline.id === contentType
        )
        return guideline ? guideline.value : ''
    }, [contentType])


   const loadStatus = useLoadStatus()

    // Create dropdown options for scenario sources - show individual PDFs
    const sourceOptions = useMemo(() => {
        // Only show dropdown for ScenarioDesk
        if (!isScenario || contentType !== CONTENT_TYPES.SCENARIO_DESK) {
            return []
        }
        
        // Use centralized function to get PDF options for selected programs only
        // Pass dbPrograms to use database programs when available
        const list = getPdfOptionsForPrograms(previewId ? dbPrograms?.GUIDELINES_ID : selectedLenders, dbPrograms)
        if(!previewId && list?.length) {
            setCurrentPdfFile(list[0].pdfPath)
            setSelectedSource(list[0].pdfPath)
        }
        return list;
    }, [isScenario, contentType, selectedLenders, dbPrograms, previewId])

    // Handle source selection for scenario dropdown
    const handleSourceChange = useCallback((value) => {
        if (isScenario && sourceOptions.length > 0) {
            const selectedOption = sourceOptions.find(option => option.value === value)
            // Only update selectedSource if we found a valid option
            let filePath = value;
            if(programs.data.MAP_GUIDELINE_FILE_NAME[value]) {
                // "reference“ 点击传递的value是fileName，所以要通过“fileName”取”filePath“并转换成s3的地址
               filePath = convertS3PathToUrl(programs.data.MAP_GUIDELINE_FILE_NAME[value].filePath)
            }
            setSelectedSource(filePath)
            if (selectedOption && selectedOption.pdfPath) {
                // Load the selected PDF directly
                // Check if it's already an absolute URL (S3 URL)
                const absolutePdfPath = selectedOption.pdfPath.startsWith('http') 
                    ? selectedOption.pdfPath 
                    : `${window.location.origin}${selectedOption.pdfPath}`
                loadStatus.setError(false);
                setCurrentPdfFile(absolutePdfPath)
            } 
        } else {
            // For non-scenario cases, update selectedSource normally
            setSelectedSource(value)
        }
    }, [isScenario, sourceOptions])

    useImperativeHandle(ref, () => ({
        handleSourceChange, // 右侧对话框，也会涉及变更pdf预览变更
        getLoadStatus: loadStatus.getLoadStatus, // 获取当前加载状态
    }), [handleSourceChange])

    // Auto-load first PDF for ScenarioDesk when no PDF is currently loaded or when source options change
    useEffect(() => {
        if (contentType === CONTENT_TYPES.SCENARIO_DESK && sourceOptions.length > 0) {
            // const FILE_PATH_MAP = getFilePathName(currentPdfFile)
            // 若pdf是后端返回的，则可以set 该 pdf url 预览
            // const target = programs.data.MAP_GUIDELINE_FILE_PATH?.[FILE_PATH_MAP]
            // if(target && target?.isHidden) {
            //    // Try S3 path first (only if it's a valid PDF path), otherwise fallback to local path
            //    const s3Url = convertS3PathToUrl(target.filePath, target.fileName);
            //    const hiddenPdfPath = s3Url || `/scenario_pdfs/${target.fileName}`;
            //    return setSelectedSource(hiddenPdfPath);
            // }
            // // If no PDF is loaded or the current PDF is not in the available options, load the first one
            // const currentPdfInOptions = sourceOptions.some(option => 
            //     currentPdfFile && currentPdfFile.includes(option.pdfPath)
            // )
            
            if (!currentPdfFile) {
                const firstOption = sourceOptions[0]
                if (firstOption && firstOption.pdfPath) {
                    // Check if it's already an absolute URL (S3 URL)
                    const absolutePdfPath = firstOption.pdfPath.startsWith('http') 
                        ? firstOption.pdfPath 
                        : `${window.location.origin}${firstOption.pdfPath}`
                    setCurrentPdfFile(absolutePdfPath)
                    setSelectedSource(firstOption.value)
                }
            }
        }
    }, [contentType, sourceOptions, currentPdfFile])

    const isFirstPage = useMemo(
        () => currentPageNumber <= 1,
        [currentPageNumber]
    )
    const isLastPage = useMemo(
        () => currentPageNumber >= numPages,
        [currentPageNumber, numPages]
    )

    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect
                // Check if containerRef.current exists before accessing its properties
                if (containerRef.current) {
                    const offsetWidth = containerRef.current.offsetWidth
                    const clientWidth = containerRef.current.clientWidth
                    // Subtract scrollbar width from container width
                    const scrollbarWidth = offsetWidth - clientWidth
                    setContainerSize({ width: width - scrollbarWidth, height })
                }
            }
        })

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current)
        }

        return () => {
            if (containerRef.current) {
                resizeObserver.unobserve(containerRef.current)
            }
        }
    }, [])

    useEffect(() => {
        prevPages.current = pages
    }, [pages])

    useEffect(() => {
        let isMounted = true
        const processFile = async () => {
            if (currentPdfFile && isMounted) {
                // Skip if this file is already being processed
                if (lastProcessedFile.current === currentPdfFile) {
                    return
                }
                
                lastProcessedFile.current = currentPdfFile
                setIsLoading(true)
                if (onPdfLoadingChange) {
                    onPdfLoadingChange(true) // Notify parent component that PDF is loading
                }
                setCurrentPageNumber(1)
                setInputPageNumber('1')
                setHighlightPageNumber(null)
                setHighlightCoordinates([])

                const result = await Worker.processFile(currentPdfFile, 1)
                if (result.type === 'success' && isMounted) {
                    setNumPages(result.numPages)
                    setPages(result.pages)
                    
                    // Initialize scale with the first page's scale
                    if (containerSize.width > 0 && containerSize.height > 0) {
                        const initialScale = calculateScale(
                            result.pages[0],
                            containerSize.width,
                            containerSize.height
                        )
                        setScale(initialScale)
                    }

                    setIsLoading(false)
                    if (onPdfLoadingChange) {
                        onPdfLoadingChange(false) // Notify parent component that PDF loading is complete
                    }
                    lastProcessedFile.current = null // Clear after successful processing
                    
                    // Update selectedSource when PDF loads successfully
                    // Only update if we don't already have a selectedSource to avoid UI flicker
                    if (isScenario && sourceOptions.length > 0 && currentPdfFile && !selectedSource) {
                        const matchingOption = sourceOptions.find(option => 
                            currentPdfFile.includes(option.pdfPath) || 
                            option.pdfPath.includes(currentPdfFile.split('/').pop())
                        )
                        if (matchingOption) {
                            setSelectedSource(matchingOption.value)
                        }
                    }
                    
                    // Execute pending jump if exists
                    if (pendingJump) {
                        // Longer delay to ensure List component is mounted
                        setTimeout(() => executePendingJump(pendingJump), 300)
                    }
                } else if (result.type === 'error' && isMounted) {
                    console.error('Error loading PDF:', result.error)
                    setIsLoading(false)
                    if (onPdfLoadingChange) {
                        onPdfLoadingChange(false) // Notify parent component that PDF loading failed
                    }
                    lastProcessedFile.current = null // Clear after error
                    setPendingJump(null) // Clear pending jump on error
                    setShowControls(true) // Show controls on error
                }
            }
        }

        processFile()

        return () => {
            isMounted = false
            lastProcessedFile.current = null // 需要回收
        }
    }, [currentPdfFile])

    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0)
        }
    }, [scale, pages])

    // Subscribe to the jump event
    jumpEmitter.useSubscription((val) => {
        const { pageNumber, pageCoordinates, dynamicPdf } = val
        console.log('jumpEmitter received:', { pageNumber, pageCoordinates, dynamicPdf })
        
        // Handle dynamic PDF loading
        if (dynamicPdf) {
            setShowControls(false) // Hide controls immediately when starting dynamic PDF load
            setCurrentPdfFile(dynamicPdf)
            
            // Update selectedSource to match the PDF being loaded
            // if (isScenario && sourceOptions.length > 0) {
            //     const matchingOption = sourceOptions.find(option => 
            //         dynamicPdf.includes(option.pdfPath) || 
            //         option.pdfPath.includes(dynamicPdf.split('/').pop())
            //     )
            //     if (matchingOption && selectedSource !== matchingOption.value) {
            //         setSelectedSource(matchingOption.value)
            //     }
            // }
            
            // Store the jump parameters to be executed after PDF loads
            setPendingJump({ pageNumber, pageCoordinates })
            return
        }
        
        // Original logic for static PDFs
        setShowControls(false) // Hide controls during static PDF jump too
        setHighlightCoordinates(pageCoordinates)
        if (Array.isArray(pageCoordinates) && pageCoordinates.length > 0) {
            setHighlightPageNumber(pageNumber)
        }
        
        // Use callback to show controls after positioning is complete
        scrollToPage(pageNumber, 0, () => {
            setShowControls(true)
        })
    })

    useEffect(() => {
        if (isMobile && pages.length > 0 && firstPageRendered) {
            // Ensure PDF content is rendered
            mountEmitter.emit()
        }
        
        // Fallback: execute pending jump when first page is rendered
        if (firstPageRendered && pendingJump && !isLoading) {
            setTimeout(() => {
                if (pendingJump) { // Check if still pending
                    executePendingJump(pendingJump)
                }
            }, 50)
        }
    }, [isMobile, pages, firstPageRendered, pendingJump, isLoading])

    const calculateScale = useCallback(
        (page, containerWidth, containerHeight) => {
            if (!page) return 1

            const pageWidth = page.viewport.width
            const pageHeight = page.viewport.height

            // Prioritize width adaptation
            let newScale = containerWidth / pageWidth

            // Ensure the scale is within the allowed range
            newScale = Math.max(newScale, MIN_SCALE)
            newScale = Math.min(newScale, MAX_SCALE)

            return newScale
        },
        []
    )

    useEffect(() => {
        if (
            pages.length > 0 &&
            containerSize.width > 0 &&
            containerSize.height > 0
        ) {
            const newScale = calculateScale(
                pages[0],
                containerSize.width,
                containerSize.height
            )
            if (
                Math.abs(newScale - scale) >= SCALE_STEP ||
                pages[0] !== prevPages.current[0]
            ) {
                setScale(newScale)
            }
        }
    }, [pages, containerSize, calculateScale])

    const getItemDimensions = useCallback(
        (index) => {
            if (!pages[index]) return { width: 0, height: 0 }
            const { width, height } = pages[index].viewport
            return {
                width: width * scale,
                height: height * scale + PAGE_GAP,
            }
        },
        [pages, scale]
    )

    /**
     * VariableSizeList needs a function to get the size of each item
     */
    const getItemSize = useCallback(
        (index) => getItemDimensions(index).height,
        [getItemDimensions]
    )

    const scrollToPage = useCallback(
        (pageNumber, retryCount = 0, onComplete = null) => {
            if (listRef.current && pages.length > 0) {
                listRef.current.scrollToItem(pageNumber - 1, 'start')
                setCurrentPageNumber(pageNumber)
                setInputPageNumber(pageNumber.toString())
                
                // Call completion callback after positioning is done
                if (onComplete) {
                    setTimeout(() => onComplete(), 100) // Small delay to ensure positioning is visually complete
                }
            } else if (retryCount < 10) {
                // Retry after a short delay
                setTimeout(() => scrollToPage(pageNumber, retryCount + 1, onComplete), 100)
            } else {
                // Even if failed, call completion callback to show controls
                if (onComplete) {
                    onComplete()
                }
            }
        },
        [pages.length]
    )

    // Execute pending jump - extracted common logic
    const executePendingJump = useCallback((jumpData) => {
        if (jumpData) {
            setHighlightCoordinates(jumpData.pageCoordinates || [])
            setPendingJump(null)
            
            // Use callback to show controls only after positioning is complete
            scrollToPage(jumpData.pageNumber, 0, () => {
                setShowControls(true)
            })
        }
    }, [scrollToPage])

    const { MAP_LENDER_PROGRAM_ID } = programs.data;
    const SOURCE_OPTIONS = useMemo(
        () =>
            sourceOptions
            .map((v) => {
                const name = MAP_LENDER_PROGRAM_ID?.[v?.programId]?.lenderDisplayName
                return {
                    value: v.value,
                    name: v.label,
                    fullName: `${name} - ${v.label}`, // 用于快速检索
                    label: (
                    <div className="select-guideline-item">
                        <img src={ICON[name]} />
                        <span>{name}</span>
                        &nbsp;-&nbsp;
                        <span>{v.label}</span>
                    </div>
                    ),
                }
            }),
        [sourceOptions, MAP_LENDER_PROGRAM_ID],
    );

    useEdgeWheelDetect(
        listRef,
        () => { 
            if(isLoading) return;
            const i = SOURCE_OPTIONS.findIndex(v => v.value === selectedSource);
            const path = SOURCE_OPTIONS?.[i-1]?.value
            path && handleSourceChange(path);
        },
        () => {
            if(isLoading) return;
            const i = SOURCE_OPTIONS.findIndex(v => v.value === selectedSource);
            i !== SOURCE_OPTIONS.length -1 && handleSourceChange(SOURCE_OPTIONS[i+1].value);
        },
    )

    const handleScroll = useCallback(() => {
        if (!listRef.current || isZooming) return

        const listElement = listRef.current._outerRef
        const scrollTop = listElement.scrollTop
        const clientHeight = listElement.clientHeight

        let totalHeight = 0
        let newPageNumber = 1

        for (let i = 0; i < pages.length; i++) {
            const pageHeight = getItemSize(i)
            const pageTop = totalHeight
            const pageBottom = pageTop + pageHeight

            if (scrollTop + clientHeight / 2 < pageBottom) {
                newPageNumber = i + 1
                break
            }

            totalHeight += pageHeight
        }

        if (newPageNumber !== currentPageNumber) {
            setCurrentPageNumber(newPageNumber)
            setInputPageNumber(newPageNumber.toString())
        }
    }, [currentPageNumber, getItemSize, pages.length])

    const handlePrevious = useCallback(() => {
        const newPageNumber = Math.max(currentPageNumber - 1, 1)
        scrollToPage(newPageNumber)
    }, [scrollToPage, currentPageNumber])

    const handleNext = useCallback(() => {
        const newPageNumber = Math.min(currentPageNumber + 1, numPages)
        scrollToPage(newPageNumber)
    }, [scrollToPage, numPages, currentPageNumber])

    const handleInputChange = useCallback(
        (e) => {
            const value = e.target.value
            if (value === '') {
                setInputPageNumber('')
                return
            }
            if (/^\d+$/.test(value)) {
                const newValue = parseInt(value, 10)
                if (newValue === 0) {
                    setInputPageNumber('')
                } else if (newValue > numPages) {
                    setInputPageNumber(numPages.toString())
                } else {
                    setInputPageNumber(newValue.toString())
                }
            }
        },
        [numPages, setInputPageNumber]
    )

    const handleInputKeyPress = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                const newPage = parseInt(inputPageNumber, 10) || 1
                if (newPage >= 1 && newPage <= numPages) {
                    scrollToPage(newPage)
                } else {
                    setInputPageNumber(currentPageNumber.toString())
                }
            }
        },
        [
            scrollToPage,
            numPages,
            currentPageNumber,
            inputPageNumber,
            setInputPageNumber,
        ]
    )

    const handleInputBlur = useCallback(() => {
        if (
            inputPageNumber === '' ||
            parseInt(inputPageNumber, 10) < 1 ||
            parseInt(inputPageNumber, 10) > numPages
        ) {
            setInputPageNumber(currentPageNumber.toString())
        }
    }, [numPages, currentPageNumber, inputPageNumber])

    const handleZoom = useCallback(
        (newScale) => {
            if (!listRef.current) return

            const listElement = listRef.current._outerRef
            const containerHeight = listElement.clientHeight
            const containerWidth = listElement.clientWidth
            const currentScrollTop = listElement.scrollTop
            const currentScrollLeft = listElement.scrollLeft

            // Calculate the center point of the current page relative to the container's visible area
            let currentPageTop = 0
            for (let i = 0; i < currentPageNumber - 1; i++) {
                currentPageTop += getItemDimensions(i).height
            }
            const { width: currentPageWidth, height: currentPageHeight } =
                getItemDimensions(currentPageNumber - 1)
            const currentPageLeft = Math.max(
                0,
                (containerWidth - currentPageWidth) / 2
            )

            // Calculate the center point of the current page
            const currentPageVerticalCenter =
                currentPageTop + currentPageHeight / 2
            const currentPageHorizontalCenter =
                currentPageLeft + currentPageWidth / 2

            // Calculate the relative position of the viewport center to the page center
            const relativeVerticalPosition =
                (currentPageVerticalCenter - currentScrollTop) / containerHeight
            const relativeHorizontalPosition =
                (currentPageHorizontalCenter - currentScrollLeft) /
                containerWidth

            setIsZooming(true)
            setScale((prevScale) => {
                // Adjust the center point of the current page after zooming
                requestAnimationFrame(() => {
                    const scaleFactor = newScale / prevScale
                    const newPageTop = currentPageTop * scaleFactor
                    const newPageLeft = Math.max(
                        0,
                        (containerWidth - currentPageWidth * scaleFactor) / 2
                    )
                    const newPageHeight = currentPageHeight * scaleFactor
                    const newPageWidth = currentPageWidth * scaleFactor
                    const newPageVerticalCenter = newPageTop + newPageHeight / 2
                    const newPageHorizontalCenter =
                        newPageLeft + newPageWidth / 2

                    const newScrollTop =
                        newPageVerticalCenter -
                        relativeVerticalPosition * containerHeight
                    const newScrollLeft =
                        newPageHorizontalCenter -
                        relativeHorizontalPosition * containerWidth

                    listElement.scrollTop = newScrollTop
                    listElement.scrollLeft = newScrollLeft
                })

                return newScale
            })
            setIsZooming(false)
        },
        [currentPageNumber, getItemDimensions]
    )

    const drawHighlights = useCallback(
        (canvas) => {
            if (!canvas) return

            if (
                Array.isArray(highlightCoordinates) &&
                highlightCoordinates.length > 0
            ) {
                // Create a new canvas for highlights
                const ctx = canvas.getContext('2d')
                ctx.fillStyle = 'rgba(198, 50, 235, 0.2)'

                highlightCoordinates.forEach((polygon) => {
                    ctx.beginPath()
                    polygon.forEach((point, index) => {
                        const x = point[0] * canvas.width
                        const y = point[1] * canvas.height
                        if (index === 0) {
                            ctx.moveTo(x, y)
                        } else {
                            ctx.lineTo(x, y)
                        }
                    })
                    ctx.closePath()
                    ctx.fill()
                })
            }
        },
        [highlightCoordinates]
    )

    function getLabelRender(labelProps) {
        const filePathName = getFilePathName(selectedSource)
        const target = programs.data?.MAP_GUIDELINE_FILE_PATH?.[filePathName]
        // 若 “isHidden” 为true 仅渲染名称
        if(target?.isHidden){
            return <span className='hide-lender-pdf'>📄&nbsp;&nbsp;&nbsp;&nbsp;{target?.displayName}</span>
        }
        if(!labelProps?.label){
            const name = selectedSource?.split?.('.pdf')?.[0]?.replace?.(/[_-]/g, ' ');
            return <span className='hide-lender-pdf'>📄&nbsp;&nbsp;&nbsp;&nbsp;{name}</span>
        }
        return labelProps?.label
    }

    // 等 David 看下需不需要 搜索功能
    // function showSearchGuideline(input, option){
    //     return input ? option.fullName.includes(input) : true;
    // }

   const fn = useRef();

    const Row = useCallback(
        ({ index, style }) => {
            const page = pages[index]
            const pageNumber = index + 1
            const { width: pageWidth, height: pageHeight } =
                getItemDimensions(index)

            fn.current = () => {
                if (pageNumber !== 1 && pageNumber !== highlightPageNumber) {
                    return;
                }

                if (!pageRefs.current) return

                if (pageNumber === 1) {
                    setFirstPageRendered(true)
                }
                if (highlightPageNumber && pageNumber === highlightPageNumber) {
                    const canvas =
                        pageRefs.current[highlightPageNumber]?.querySelector(
                            'canvas'
                        )
                    drawHighlights(canvas)
                }
            }

            return (
                <div
                    style={{
                        ...style,
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        minWidth: `${pageWidth}px`,
                        paddingBottom: `${PAGE_GAP}px`,
                        height: `${pageHeight - PAGE_GAP}px`,
                        transition: 'all 0.3s ease',
                    }}
                    ref={(el) => (pageRefs.current[pageNumber] = el)}
                    data-page-number={pageNumber}
                >
                    {page ? (
                        <PageRender
                            key={`page_${pageNumber}`}
                            pageNumber={pageNumber}
                            scale={scale}
                            renderAnnotationLayer={false}
                            onRenderSuccess={fn}
                        />
                    ) : (
                        <div
                            style={{
                                width: `${pageWidth}px`,
                                height: `${pageHeight - PAGE_GAP}px`,
                                backgroundColor: '#f9f9f9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ccc',
                                fontSize: '14px',
                            }}
                        >
                            Loading Page {pageNumber}...
                        </div>
                    )}
                </div>
            )
        },
        [
            drawHighlights,
            pages,
            scale,
            highlightPageNumber,
            pageRefs,
            getItemDimensions,
        ]
    )

    const LIST_HEIGHT = containerRef.current?.clientHeight;
    const SHOW_SUFFIX_ICON = SOURCE_OPTIONS?.length > 1 ? undefined : null;

    // Show error message if react-pdf Document component failed to load PDF
    // Note: isLoading is handled separately through onPdfLoadingChange callback
    if (loadStatus.error) {
        return (
            <div className={`pdf-viewer-wrapper ${(IS_IFRAMES || IS_GUIDELINE_GPT) ? 'iframe-pdf-viewer-wrapper-adjuster' : ''}`} style={{ position: 'relative' }}>
                {/* Loading overlay covering entire left panel - unified loading for both PDF and answer generation */}
                {isPdfLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                        pointerEvents: 'auto'
                    }}>
                        <Spin size="large" />
                    </div>
                )}
                {/* Scenario Source Dropdown - positioned above PDF viewer - only for ScenarioDesk */}
                {contentType === CONTENT_TYPES.SCENARIO_DESK && sourceOptions.length > 0 && (
                    <div className="scenario-source-dropdown">
                        <Select 
                            // showSearch
                            // filterOption={showSearchGuideline}
                            suffixIcon={SHOW_SUFFIX_ICON}
                            placeholder="All PDFs"
                            popupClassName={`scenario-source-dropdown-ctx ${(IS_IFRAMES || IS_GUIDELINE_GPT) ? 'scenario-source-dropdown-h' : ''}`}
                            value={selectedSource}
                            onChange={handleSourceChange}
                            style={{ width: '100%' }}
                            options={SOURCE_OPTIONS}
                            labelRender={getLabelRender}
                            listHeight={LIST_HEIGHT}
                        />
                    </div>
                )}
                
                <div className="pdf-container" ref={containerRef}>
                    <div
                        className="p-2 pdf-container-error"
                        style={{ 
                            minHeight: '100%', 
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                    >
                        <img src={ICON_ERROR} width={40} />
                        <p>Failed to load guideline</p>
                        <p>This guideline isn't available right now. </p>
                        <p>Please try again later or switch to another guideline. </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={`pdf-viewer-wrapper ${(IS_IFRAMES || IS_GUIDELINE_GPT) ? 'iframe-pdf-viewer-wrapper-adjuster' : ''}`} style={{ position: 'relative' }}>
            {/* Loading overlay covering entire left panel */}
            {isPdfLoading && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                    pointerEvents: 'auto'
                }}>
                    <Spin size="large" />
                </div>
            )}
            {/* Scenario Source Dropdown - positioned above PDF viewer - only for ScenarioDesk */}
            {contentType === CONTENT_TYPES.SCENARIO_DESK && sourceOptions.length > 0 && (
                <div className="scenario-source-dropdown">
                    <Select
                        // showSearch
                        // filterOption={showSearchGuideline}
                        suffixIcon={SHOW_SUFFIX_ICON}
                        popupClassName={`scenario-source-dropdown-ctx ${(IS_IFRAMES || IS_GUIDELINE_GPT) ? 'scenario-source-dropdown-h' : ''}`}
                        placeholder={<div className="d-flex justify-content-between">
                            <div style={{fontSize: '16px', color: '#222222'}}>All PDFs</div>
                            <div>{sourceOptions.length} PDFs</div>
                        </div>}
                        value={selectedSource}
                        onChange={handleSourceChange}
                        style={{ width: '100%' }}
                        allowClear={false}
                        options={SOURCE_OPTIONS}
                        labelRender={getLabelRender}
                        listHeight={LIST_HEIGHT}
                    />
                </div>
            )}
            
            <div className="pdf-container" ref={containerRef}>
                {isMobile && showControls && (
                    <div className="pdf-controls-mobile">
                        <span className="guideline-name">{guidelineName}</span>
                        <div className="page-navigation">
                            <button
                                onClick={handlePrevious}
                                disabled={isFirstPage}
                                className="pdf-controls-button"
                            >
                                <LeftOutlined
                                    style={{
                                        color: isFirstPage ? '#d9d9d9' : '#6e6e70',
                                        cursor: isFirstPage
                                            ? 'not-allowed'
                                            : 'pointer',
                                    }}
                                />
                            </button>
                            <Input
                                value={inputPageNumber}
                                onChange={handleInputChange}
                                onKeyPress={handleInputKeyPress}
                                onBlur={handleInputBlur}
                                className="page-input"
                                placeholder="1"
                            />
                            <span className="pdf-page-number">/{numPages}</span>
                            <button
                                onClick={handleNext}
                                disabled={isLastPage}
                                className="pdf-controls-button"
                            >
                                <RightOutlined
                                    style={{
                                        color: isLastPage ? '#d9d9d9' : '#6e6e70',
                                        cursor: isLastPage
                                            ? 'not-allowed'
                                            : 'pointer',
                                    }}
                                />
                            </button>
                        </div>
                    </div>
                )}
                
                <Document 
                    loading=' '
                    file={currentPdfFile}
                    onLoadError={loadStatus.onLoadError}
                    onLoadSuccess={loadStatus.onLoadSuccess}
                    options={{
                        cMapUrl: 'cmaps/',
                        cMapPacked: true,
                        // Disable range requests to reduce multiple network calls
                        disableRange: true,
                        // Disable streaming to avoid multiple requests
                        disableStream: true,
                    }}
                >
                {numPages && (
                    <List
                        ref={listRef}
                        width={containerSize.width}
                        height={containerSize.height}
                        itemCount={numPages}
                        itemSize={getItemSize}
                        onScroll={handleScroll}
                        style={{
                            willChange: 'transform',
                            overscrollBehavior: 'none',
                        }}
                    >
                        {Row}
                    </List>
                )}
            </Document>
            {isMobile ? (
                <div className="chat-floating-wrapper">
                    <div
                        className="ai-chat-button"
                        onClick={() => setMobileAIChat(true)}
                    >
                        <img src="/images/ai-chat.svg" alt="AI Chat" />
                        <span>Chat</span>
                    </div>
                </div>
            ) : (
                showControls && !anomaly?.DATABASE_ERROR && <div className="pdf-controls">
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: '30px',
                        }}
                    >
                        <button
                            onClick={handlePrevious}
                            disabled={isFirstPage}
                            className="pdf-controls-button"
                            style={{
                                width: '20px',
                                height: '20px',
                                marginRight: '8px',
                            }}
                        >
                            <LeftOutlined />
                        </button>
                        <Input
                            value={inputPageNumber}
                            onChange={handleInputChange}
                            onKeyPress={handleInputKeyPress}
                            onBlur={handleInputBlur}
                            className="pdf-controls-input"
                            placeholder="1"
                        />
                        <span style={{ marginLeft: '8px' }}>/{numPages}</span>
                        <button
                            onClick={handleNext}
                            disabled={isLastPage}
                            className="pdf-controls-button"
                            style={{
                                width: '20px',
                                height: '20px',
                                marginLeft: '8px',
                            }}
                        >
                            <RightOutlined />
                        </button>
                    </div>
                    <div className="pdf-controls-divider"></div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '30px',
                        }}
                    >
                        <button
                            onClick={() =>
                                handleZoom(
                                    Math.max(scale - SCALE_STEP, MIN_SCALE)
                                )
                            }
                            className="pdf-controls-button"
                            style={{ width: '30px', height: '30px' }}
                            disabled={scale <= MIN_SCALE || isZooming}
                        >
                            <MinusOutlined />
                        </button>
                        <span style={{ marginLeft: '8px', marginRight: '8px' }}>
                            {Math.round(scale * 100)}%
                        </span>
                        <button
                            onClick={() =>
                                handleZoom(
                                    Math.min(scale + SCALE_STEP, MAX_SCALE)
                                )
                            }
                            className="pdf-controls-button"
                            style={{ width: '30px', height: '30px' }}
                            disabled={scale >= MAX_SCALE || isZooming}
                        >
                            <PlusOutlined />
                        </button>
                    </div>
                </div>
            )}
            </div>
        </div>
    )
}

export default React.forwardRef(PDFViewer)
